// DateRangePicker.jsx
import React from "react";
import { Box, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const DateRangePicker = ({ fromDate, setFromDate, toDate, setToDate, minDate, maxDate }) => {
  const handleFromDateChange = (newValue) => {
    if (newValue) {
      setFromDate(dayjs(newValue).format("YYYY-MM-DD"));
    } else {
      setFromDate("");
    }
  };

  const handleToDateChange = (newValue) => {
    if (newValue) {
      setToDate(dayjs(newValue).format("YYYY-MM-DD"));
    } else {
      setToDate("");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "600",
            color: "#334155",
            mb: 1,
            fontFamily: "Host Grotesk, sans-serif",
          }}
        >
          Date Range
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <DatePicker
            label="From Date"
            value={fromDate ? dayjs(fromDate) : null}
            onChange={handleFromDateChange}
            format="DD-MM-YYYY"
            minDate={minDate}
            maxDate={maxDate}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "Host Grotesk, sans-serif",
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                    transition: "all 0.2s ease",
                    "&:hover fieldset": {
                      borderColor: "#0f766e",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0f766e",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Host Grotesk, sans-serif",
                    color: "#64748b",
                    fontWeight: 500,
                    "&.Mui-focused": {
                      color: "#0f766e",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "Host Grotesk, sans-serif",
                    fontSize: "0.875rem",
                    color: "#334155",
                  },
                  "& .MuiIconButton-root": {
                    color: "#0f766e",
                    "&:hover": {
                      backgroundColor: "rgba(15, 118, 110, 0.08)",
                    },
                  },
                },
              },
              popper: {
                sx: {
                  "& .MuiPaper-root": {
                    borderRadius: "12px",
                    boxShadow: "0 12px 40px rgba(15, 118, 110, 0.15)",
                    border: "1px solid rgba(15, 118, 110, 0.1)",
                    fontFamily: "Host Grotesk, sans-serif",
                  },
                  "& .MuiPickersCalendarHeader-root": {
                    fontFamily: "Host Grotesk, sans-serif",
                  },
                  "& .MuiPickersDay-root": {
                    fontFamily: "Host Grotesk, sans-serif",
                    "&.Mui-selected": {
                      backgroundColor: "#0f766e",
                      "&:hover": {
                        backgroundColor: "#0d5b57",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "rgba(15, 118, 110, 0.1)",
                    },
                  },
                  "& .MuiPickersYear-yearButton": {
                    fontFamily: "Host Grotesk, sans-serif",
                    "&.Mui-selected": {
                      backgroundColor: "#0f766e",
                      "&:hover": {
                        backgroundColor: "#0d5b57",
                      },
                    },
                  },
                },
              },
            }}
          />
          <DatePicker
            label="To Date"
            value={toDate ? dayjs(toDate) : null}
            onChange={handleToDateChange}
            format="DD-MM-YYYY"
            minDate={minDate}
            maxDate={maxDate}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "Host Grotesk, sans-serif",
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                    transition: "all 0.2s ease",
                    "&:hover fieldset": {
                      borderColor: "#0f766e",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0f766e",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Host Grotesk, sans-serif",
                    color: "#64748b",
                    fontWeight: 500,
                    "&.Mui-focused": {
                      color: "#0f766e",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "Host Grotesk, sans-serif",
                    fontSize: "0.875rem",
                    color: "#334155",
                  },
                  "& .MuiIconButton-root": {
                    color: "#0f766e",
                    "&:hover": {
                      backgroundColor: "rgba(15, 118, 110, 0.08)",
                    },
                  },
                },
              },
              popper: {
                sx: {
                  "& .MuiPaper-root": {
                    borderRadius: "12px",
                    boxShadow: "0 12px 40px rgba(15, 118, 110, 0.15)",
                    border: "1px solid rgba(15, 118, 110, 0.1)",
                    fontFamily: "Host Grotesk, sans-serif",
                  },
                  "& .MuiPickersCalendarHeader-root": {
                    fontFamily: "Host Grotesk, sans-serif",
                  },
                  "& .MuiPickersDay-root": {
                    fontFamily: "Host Grotesk, sans-serif",
                    "&.Mui-selected": {
                      backgroundColor: "#0f766e",
                      "&:hover": {
                        backgroundColor: "#0d5b57",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "rgba(15, 118, 110, 0.1)",
                    },
                  },
                  "& .MuiPickersYear-yearButton": {
                    fontFamily: "Host Grotesk, sans-serif",
                    "&.Mui-selected": {
                      backgroundColor: "#0f766e",
                      "&:hover": {
                        backgroundColor: "#0d5b57",
                      },
                    },
                  },
                },
              },
            }}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
